// ---------------------------------------------------------------------------
// The standing, which is the closing slide of every reveal.
//
// There is no scoreboard phase and there must not be one again — see CLAUDE.md
// — so this slide *is* the standing between two rounds, and there is no second
// place in the app that draws one. `RoundScores` is the only export for that
// reason: anything that ever wants a standing again should come through here
// rather than build a second one that can disagree with it.
//
// The teams path is a whole component of its own rather than a branch inside
// the solo one, because a team total is a best-of and not a sum, so its row has
// to say which member is being paid for. `ScoresHeader` is shared by both, and
// it is what keeps the two paths making the same claim about the best answer.
//
// It takes `state` and `round` rather than a `RevealView`, and that is not an
// oversight: a standing is the score table and nothing about a cursor, a queue
// or a released set. Do not hand it the view to save two props.
// ---------------------------------------------------------------------------

import type {
  BestAnswer,
  ClientGameState,
  ClientRound,
  PublicPlayer,
  Team,
} from '@countdown/shared';
import { ScoreSpread } from '../../../components/charts/ScoreSpread.js';
import { CheckIcon, Icon } from '../../../components/primitives/Icon.js';
import { PlayerChip } from '../../../components/primitives/PlayerChip.js';
import { ScoreRow } from '../../../components/primitives/ScoreRow.js';
import type { ScoreRowSize } from '../../../components/primitives/ScoreRow.js';
import { ScoreTotal } from '../../../components/primitives/ScoreTotal.js';
import { Frame } from '../../../components/round/atoms.js';
import { AudienceTag } from '../../../components/round/audience.js';
import { listNames } from '../../../lib/format.js';
import {
  displayColorOf,
  membersOfTeam,
  scoreFor,
  seatedPlayers,
} from '../../../lib/playerLookup.js';
import { conundrumIsRaced } from './aggregate.js';
import { STAGE_FILL } from './atoms.js';
import { reasonForSubmission, rejected } from './ruling.js';

/**
 * How many rows a standing draws before it becomes a top-ten plus you.
 *
 * Ten because that is what a leaderboard means to everybody who has ever seen
 * one, and because the row scale already bottoms out at `sm` — nine 44px rows
 * fit a phone with thirteen pixels to spare, and a tenth is the first one that
 * does not. Past this the list is not shortened, it is replaced: see
 * `LargeRoomScores`.
 */
const STANDING_ROWS = 10;

// --- Scores ----------------------------------------------------------------

interface TeamEntry {
  kind: 'team';
  team: Team;
  total: number;
  gain: number;
  members: { player: PublicPlayer; score: number; carried: boolean }[];
}

interface SoloEntry {
  kind: 'player';
  player: PublicPlayer;
  total: number;
  gain: number;
}

/**
 * Team totals, which are a best-of and not a sum.
 *
 * The member list is one line each rather than a row each, and the line the
 * whole thing exists for — which member's answer the team is being paid for —
 * is said twice on purpose: once by name in the team's own header, and once by
 * the mark against that member. A team total that is not the sum of its members
 * reads as arithmetic going wrong unless the screen says otherwise.
 */
function TeamRoundScores({
  state,
  round,
  title,
}: {
  state: ClientGameState;
  round: ClientRound;
  title: string;
}) {
  const entries: (TeamEntry | SoloEntry)[] = [];

  for (const team of state.teams) {
    const members = membersOfTeam(state, team.id).filter((player) => player.role === 'player');
    if (members.length === 0) continue;
    const scored = members.map((player) => ({
      player,
      score: round.submissions[player.id]?.score ?? 0,
    }));
    const gain = scored.reduce((highest, entry) => Math.max(highest, entry.score), 0);
    entries.push({
      kind: 'team',
      team,
      total: state.teamScores[team.id] ?? 0,
      gain,
      members: scored.map((entry) => ({ ...entry, carried: gain > 0 && entry.score === gain })),
    });
  }

  for (const player of seatedPlayers(state)) {
    if (player.teamId !== null) continue;
    entries.push({
      kind: 'player',
      player,
      total: scoreFor(state, player),
      gain: round.submissions[player.id]?.score ?? 0,
    });
  }

  entries.sort((a, b) => b.total - a.total);

  return (
    <Frame dense className={STAGE_FILL}>
      <ScoresHeader title={title} best={round.best} />
      <div className="flex flex-col gap-2">
        {entries.map((entry) => {
          // The same rule as the solo standing: the wash marks the side you are
          // on, not the side that is winning. On a team it is your team's row,
          // because a team is one bucket and the standing has one row for it.
          const yours =
            entry.kind === 'team'
              ? entry.members.some((member) => member.player.id === state.youId)
              : entry.player.id === state.youId;
          const skin = yours ? 'border-line-strong bg-blue-700/30' : 'border-line bg-surface-2';

          if (entry.kind === 'player') {
            return (
              <div
                key={entry.player.id}
                className={`flex items-center gap-3 rounded-card border px-3 py-2 transition-colors duration-300 ${skin}`}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <PlayerChip
                    player={entry.player}
                    color={displayColorOf(state, entry.player)}
                    size="sm"
                    showIdentity={false}
                  />
                  <span className="truncate font-mono text-[11px] text-ink-dim">
                    {reasonForSubmission(
                      round,
                      round.submissions[entry.player.id],
                      conundrumIsRaced(state),
                    )}
                  </span>
                </div>
                <ScoreTotal total={entry.total} gain={entry.gain} size="sm" className="ml-auto" />
              </div>
            );
          }

          const carriers = entry.members.filter((member) => member.carried);

          return (
            <div
              key={entry.team.id}
              className={`flex flex-col gap-2 rounded-card border px-3 py-2 transition-colors duration-300 ${skin}`}
              style={{ borderLeftWidth: '5px', borderLeftColor: entry.team.color }}
            >
              <div className="flex items-center gap-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="font-semibold">{entry.team.name}</p>
                  <span className="truncate font-mono text-[11px] text-ink-dim">
                    {carriers.length === 0
                      ? 'nobody scored this round'
                      : `best of the team, ${listNames(carriers.map((member) => member.player.displayName))}`}
                  </span>
                </div>
                <ScoreTotal total={entry.total} gain={entry.gain} size="sm" className="ml-auto" />
              </div>

              {/* Members wrap onto one shared line on a phone and take a line
                  each once there is room. Three teams of three is nine lines
                  stacked, which is 114px this screen does not have, and the
                  thing those lines are for — seeing that one member's score is
                  the team's score and the rest are not added to it — reads just
                  as well side by side. The word each of them played is the part
                  that steps back to a wider screen, because the reveal showed it
                  in full a moment ago. */}
              <ul className="flex flex-wrap gap-x-2 gap-y-1 sm:flex-col sm:flex-nowrap">
                {entry.members.map((member) => (
                  <li
                    key={member.player.id}
                    className="flex min-w-0 items-center gap-1.5 rounded-chip bg-bg-alt px-2 py-0.5 font-mono text-[11px] sm:gap-2"
                  >
                    {/* No colour, no swatch. A room past `COLORED_SEATS` has
                        none at all, and an empty circle in front of every name
                        is a colour system left switched on with nothing in it. */}
                    {displayColorOf(state, member.player) === null ? null : (
                      <span
                        aria-hidden="true"
                        className="size-1.5 shrink-0 rounded-full"
                        style={{
                          backgroundColor: displayColorOf(state, member.player) ?? undefined,
                        }}
                      />
                    )}
                    <span className="shrink-0 font-semibold">{member.player.displayName}</span>
                    <span className="hidden truncate text-ink-dim sm:inline">
                      {reasonForSubmission(
                        round,
                        round.submissions[member.player.id],
                        conundrumIsRaced(state),
                      )}
                    </span>
                    <span className="sr-only">
                      {member.carried ? 'counted for the team' : 'not counted, best-of'}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 sm:ml-auto">
                      {member.carried ? (
                        <Icon icon={CheckIcon} size={13} className="text-good" />
                      ) : null}
                      <span className={`tabular ${member.carried ? 'text-good' : 'text-ink-dim'}`}>
                        {member.score}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {/* The line saying a team is paid its best answer rather than the sum has
          gone. Every team row already names the member being paid for, beside
          the tick on that member's own score. */}
    </Frame>
  );
}

// --- A standing for a room too big to list ---------------------------------

/**
 * One placed row: the position, and the ordinary score row beside it.
 *
 * The number is the whole reason this exists. In a room of ten the standing IS
 * the positions — the list is sorted and you can count — and in a room of five
 * hundred the one fact a player wants is which of the five hundred they are, and
 * counting is not available to them.
 *
 * **Ties share a place**, worked out from the totals rather than from the index,
 * because two players level on 42 are equal second and the second of them being
 * told they are third is the standing lying about the only number on the row.
 */
function PlacedRow({
  state,
  round,
  player,
  place,
  raced,
}: {
  state: ClientGameState;
  round: ClientRound;
  player: PublicPlayer;
  place: number;
  raced: boolean;
}) {
  const submission = round.submissions[player.id];
  const gain = submission?.score ?? 0;
  const yours = player.id === state.youId;

  return (
    <li className="flex items-center gap-2" data-place={place} data-you={yours ? 'true' : undefined}>
      <span
        className={`tabular w-7 shrink-0 text-right font-mono text-[13px] font-bold ${
          yours ? 'text-accent' : 'text-ink-dim'
        }`}
      >
        {place}
      </span>
      <div className="min-w-0 flex-1">
        <ScoreRow
          showIdentity={false}
          player={player}
          color={displayColorOf(state, player)}
          total={scoreFor(state, player)}
          gain={gain > 0 ? gain : null}
          reason={reasonForSubmission(round, submission, raced)}
          reasonTone={submission !== undefined && rejected(submission) ? 'alert' : 'default'}
          you={yours}
          size="sm"
        />
      </div>
      <AudienceTag state={state} player={player} />
    </li>
  );
}

/**
 * The standing for a room drawn as an audience: the top ten, the shape of
 * everybody else, and your own row.
 *
 * **Your row is highlighted where it already is, never duplicated.** Isaac asked
 * for exactly that, and the alternative is a standing that shows one player
 * twice and leaves a reader working out whether they are third or eleventh. So
 * the top ten is drawn once; if you are in it, the wash `ScoreRow` already draws
 * is the whole of the answer, and nothing is appended. Only a player outside the
 * ten gets a second block, under a rule that says what it is.
 *
 * **The strip above the list is the four hundred and ninety rows that are not
 * drawn.** A top ten in a room of five hundred tells four hundred and ninety
 * people nothing about themselves; the spread says where in the pile they landed
 * and how much of the room is behind them. It is a `ScoreSpread` — divs, and the
 * argument for it being a silhouette rather than a `Histogram` is on that file.
 *
 * A spectator has no row and no mark, which is the truth: none of these is
 * theirs. The strip still draws, without a `you`.
 */
function LargeRoomScores({
  state,
  round,
  title,
}: {
  state: ClientGameState;
  round: ClientRound;
  title: string;
}) {
  const rows = [...seatedPlayers(state)].sort((a, b) => scoreFor(state, b) - scoreFor(state, a));
  const totals = rows.map((player) => scoreFor(state, player));
  const raced = conundrumIsRaced(state);

  // Competition ranking: the first row holding a total is the place every row
  // holding it is on. Computed once over the sorted list rather than per row.
  const placeOf = new Map<string, number>();
  rows.forEach((player, index) => {
    const total = totals[index];
    const first = totals.indexOf(total);
    placeOf.set(player.id, first + 1);
  });

  const yourIndex = rows.findIndex((player) => player.id === state.youId);
  const you = yourIndex < 0 ? null : rows[yourIndex];
  const youAreListed = yourIndex >= 0 && yourIndex < STANDING_ROWS;

  return (
    <Frame dense className={STAGE_FILL}>
      <ScoresHeader title={title} best={round.best} />

      <ScoreSpread
        values={totals}
        you={you === null ? null : scoreFor(state, you)}
        caption={rows.length === 1 ? '1 player' : `${rows.length} players`}
      />

      <ol aria-label="The standing, highest first" className="flex flex-col gap-1">
        {rows.slice(0, STANDING_ROWS).map((player) => (
          <PlacedRow
            key={player.id}
            state={state}
            round={round}
            player={player}
            place={placeOf.get(player.id) ?? 0}
            raced={raced}
          />
        ))}
      </ol>

      {you === null || youAreListed ? null : (
        <div className="flex flex-col gap-1">
          {/* Named, because a row appearing under a gap with no explanation
              reads as the eleventh place rather than as yours. */}
          <p className="label-mono">and you</p>
          <ol className="flex flex-col gap-1">
            <PlacedRow
              state={state}
              round={round}
              player={you}
              place={placeOf.get(you.id) ?? 0}
              raced={raced}
            />
          </ol>
        </div>
      )}
    </Frame>
  );
}

/**
 * The best the board allowed, small, above the standing.
 *
 * The standing is the last thing between two rounds, so this is the last chance
 * to see what was there — and the alternative was paginating backwards through
 * the reveal to find a slide that has already gone by.
 *
 * The wording is the payoff slide's own, so the two read as the same claim
 * rather than as two claims about the same round. "One that fits" and not "the
 * answer" for a conundrum: the round takes any word using all nine letters.
 */
function ScoresHeader({ title, best }: { title: string; best: BestAnswer | null }) {
  const line =
    best === null
      ? null
      : best.kind === 'letters'
        ? `the board had ${best.word.toUpperCase()}`
        : best.kind === 'numbers'
          ? `the solver had ${best.value}`
          : `one that fits ${best.word.toUpperCase()}`;

  // One line, and the best word gives way rather than wrapping. A second header
  // row is 14px, and the nine-row standing on a phone has thirteen to spare.
  return (
    <div className="flex shrink-0 items-baseline gap-x-3">
      <h2 className="shrink-0 text-lg font-bold sm:text-xl">{title}</h2>
      {line === null ? null : (
        <span className="label-mono ml-auto min-w-0 truncate">{line}</span>
      )}
    </div>
  );
}

/**
 * The round's score rows, and the closing slide of every reveal.
 *
 * It was exported for the between-rounds scoreboard, which drew the same
 * numbers a beat later through this same component so the two could not
 * disagree. There is no scoreboard now and no other caller: this slide *is* the
 * standing between two rounds. Anything that ever wants to draw a standing
 * again should come through here rather than build a second one.
 *
 * The row scale falls with the number of rows rather than the list scrolling.
 * Nine seated players is an ordinary room and the whole point of this screen is
 * that a standing fits on the thing you are holding.
 */
export function RoundScores({
  state,
  round,
  title,
  size = 'lg',
}: {
  state: ClientGameState;
  round: ClientRound;
  title: string;
  size?: ScoreRowSize;
}) {
  if (state.teams.length > 0) {
    return <TeamRoundScores state={state} round={round} title={title} />;
  }

  const rows = [...seatedPlayers(state)].sort((a, b) => scoreFor(state, b) - scoreFor(state, a));

  // **Keyed on the number of seats, not on whether the reveal was aggregated.**
  // They usually agree, and where they do not the seat count is the one that
  // matters: a host who forces `aggregateReveal: 'on'` at four players wants the
  // answers gathered up, not a top ten of four; and a room of two hundred that
  // forced it OFF still cannot draw two hundred rows.
  if (rows.length > STANDING_ROWS) {
    return <LargeRoomScores state={state} round={round} title={title} />;
  }

  // Three steps rather than two, and the steps moved, because the stage kept
  // the board strip's 71px when the strip stopped standing down for the
  // scores. Measured at 390x844 in the 501px stage that leaves: a `lg` row is
  // 73px and four fit, `md` is 70 and five fit, `sm` is 44 and nine fit with
  // thirteen to spare. Six `md` rows overrun by nine, which is why six is
  // already `sm`. The list gets smaller; it does not get a scrollbar.
  const rowSize: ScoreRowSize = rows.length > 5 ? 'sm' : rows.length > 4 ? 'md' : size;

  // The gaps come down with the rows, and for the same reason: nine 44px rows,
  // a 28px title and the frame's own padding come to 504 at six-pixel gaps and
  // 488 at four.
  const listGap = rowSize === 'sm' ? 'gap-1 sm:gap-1.5' : 'gap-1.5 sm:gap-2';

  return (
    <Frame dense className={STAGE_FILL}>
      <ScoresHeader title={title} best={round.best} />
      <div className={`flex flex-col ${listGap}`}>
        {rows.map((player) => {
          const submission = round.submissions[player.id];
          const gain = submission?.score ?? 0;
          const row = (
            <ScoreRow
              key={player.id}
              showIdentity={false}
              player={player}
              color={displayColorOf(state, player)}
              total={scoreFor(state, player)}
              gain={gain > 0 ? gain : null}
              reason={reasonForSubmission(round, submission, conundrumIsRaced(state))}
              reasonTone={submission !== undefined && rejected(submission) ? 'alert' : 'default'}
              // Your own row, not the top one. The list is already sorted, so
              // the lead is where the lead has always been; what a standing has
              // no other way of pointing at is where YOU landed in it.
              you={player.id === state.youId}
              size={rowSize}
            />
          );

          // **The audience seat wears its tag and everybody else costs
          // nothing.** The wrapper exists only in a room that has the seat
          // switched on, so an ordinary game's standing is exactly the markup it
          // was — one `ScoreRow` per child, no box around it. That is the common
          // case and it is the one most at risk from this whole change.
          if (!state.settings.audienceSeat) return row;
          return (
            <div key={player.id} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">{row}</div>
              <AudienceTag state={state} player={player} />
            </div>
          );
        })}
      </div>
    </Frame>
  );
}
